import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  PropType,
  ref,
  unref,
  watch,
} from "vue";

import VueI18n from "vue-i18n";

import {
  Logo,
} from "@/scripts/Common";

import {
  Logo as LogoType,
} from "@/model/Logo";

import {
  maximumDpi,
  maximumImageHeight,
  maximumImageWidth,
} from "@/scripts/Common/constraints/constraints";

import {
  useI18n,
} from "@/composables/useI18n";

import {
  ImageDownload,
} from "@/scripts/Common/ImageDownload";

import InformationDialog from "@/components/base/InformationDialog.vue";

import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";

import {
  isStringAndHasValue,
} from "@/scripts/Common/validators/defaultValidator";


export default /*#__PURE__*/_defineComponent({
  __name: 'LogoEdit',
  props: {
  label: {
    required: true,
    type: String as PropType<string | VueI18n.TranslateResult>,
  },
  hasNoEditRights: {
    required: true,
    type: Boolean as PropType<boolean>,
  },
  model: {
    required: true,
    type: Object as PropType<Logo | LogoType>,
  },
  dpi: {
    required: false,
    type: Number as PropType<number>,
    default: maximumDpi
  },
  maximumWidth: {
    required: false,
    type: Number as PropType<number>,
    default: maximumImageWidth
  },
  maximumHeight: {
    required: false,
    type: Number as PropType<number>,
    default: maximumImageHeight
  },
  ignoreValidation: {
    required: false,
    type: Boolean as PropType<boolean>,
    default: false
  },
  isRequired: {
    required: false,
    type: Boolean as PropType<boolean>,
    default: true
  },
},
  emits: [
  'onValidChange',
  'update:logo',
],
  setup(__props, { emit: emits }) {

const props = __props;

const {
  t
} = useI18n();





const valid = ref(true);
const errorMessage = ref('');
const imageHeight = ref(0);
const imageWidth = ref(0);

const userFilesView = ref();
const errorInformationDialog = ref();

const required = computed(() => props.isRequired);

const heightLabel = computed(() => (imageHeight.value || props.ignoreValidation) ? t('height_label') : t('required_height_label'));
const widthLabel = computed(() => (imageWidth.value || props.ignoreValidation) ? t('width_label') : t('required_width_label'));

const height = computed(() => (imageHeight.value || props.ignoreValidation) ? imageHeight.value : props.maximumHeight);
const width = computed(() => (imageWidth.value || props.ignoreValidation) ? imageWidth.value : props.maximumWidth);
const dpi = computed(() => maximumDpi)

const logo = ref<Logo>(props.model);
const logoData = computed({
  get() {
    return props.model?.data
  },
  set(value: string) {
    logo.value.data = value;
    emits('update:logo', unref(logo))
  }
})

const logoFormat = computed({
  get() {
    return props.model?.format
  },
  set(value: string) {
    logo.value.format = value;
    emits('update:logo', unref(logo))
  }
})

const validate = () => {
  valid.value = !props.isRequired || (isStringAndHasValue(logoData.value) && isStringAndHasValue(logoFormat.value))
}

const showUserFiles = () => {
  userFilesView.value.click();
}

const hasImageDesiredFormat = (width: number, height: number) => {
  return width <= props.maximumWidth && height <= props.maximumHeight;
}

const generateFormatError = (currentHeight: number, currentWidth: number) => {
  errorMessage.value = t("dialog_error_format", {
    desiredWidth: props.maximumWidth,
    desiredHeight: props.maximumHeight,
    height: currentHeight,
    width: currentWidth
  });

  showErrorDialog();
}

const generateDefaultError = () => {
  errorMessage.value = t("dialog_error_unknown").toString();
  showErrorDialog();
}

const showErrorDialog = () => errorInformationDialog.value.Show();

const readImage = (file: File) => {
  const reader = new FileReader();
  const extension = file.type.split("/").pop()?.toLowerCase().toString() ?? '';
  const image = new Image();

  image.addEventListener("load",
    () => {
      if (hasImageDesiredFormat(image.naturalWidth, image.naturalHeight)) {
        logoData.value = image.src;
        logoFormat.value = extension;
        imageHeight.value = image.naturalHeight;
        imageWidth.value = image.naturalWidth;
        return;
      }

      generateFormatError(image.naturalHeight, image.naturalWidth);
    })

  reader.addEventListener("error", () => generateDefaultError())

  reader.addEventListener("load", () => image.src = reader.result as string);

  if (file) {
    reader.readAsDataURL(file);
  }
}

const download = () => {
  if (!isStringAndHasValue(logoData.value) || !isStringAndHasValue(logoFormat.value)) return;

  const filename = `${t('logo_filename')}.${logoFormat.value}`;
  ImageDownload.Create(logoData.value, filename);
}

const removeImage = () => {
  logoData.value = '';
  logoFormat.value = '';
  imageHeight.value = 0;
  imageWidth.value = 0;
}

const onLoadImage = () => {
  const image = new Image();
  image.addEventListener("load",
    () => {
      imageHeight.value = image.naturalHeight
      imageWidth.value = image.naturalWidth
    })

  image.src = logoData.value
}

watch([required, logoData], () => validate());

watch(valid, () =>{
  emits('onValidChange', unref(valid))
})

onMounted(() => validate())


return { __sfc: true,t, emits, props, valid, errorMessage, imageHeight, imageWidth, userFilesView, errorInformationDialog, required, heightLabel, widthLabel, height, width, dpi, logo, logoData, logoFormat, validate, showUserFiles, hasImageDesiredFormat, generateFormatError, generateDefaultError, showErrorDialog, readImage, download, removeImage, onLoadImage, InformationDialog, FloatingActionButton, isStringAndHasValue }
}

})